// Passkeys in the browser (backend/passkeys.py). The backend sends WebAuthn options as JSON, with binary
// fields in base64url; the browser's API wants ArrayBuffers, and its answer goes back as JSON.

const toBuffer = (text) => {
  const b64 = text.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0)).buffer;
};

const toText = (buffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

const descriptors = (list) => (list || []).map((c) => ({ ...c, id: toBuffer(c.id) }));

export const passkeysSupported = () => typeof window !== "undefined" && !!window.PublicKeyCredential;

// A readable name for a new passkey, e.g. "Chrome on Linux".
export function deviceName() {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /iPhone|iPad/.test(ua) ? "iPhone" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac"
    : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "this device";
  return `${browser} on ${os}`;
}

// What people see when the browser's passkey prompt fails.
export function passkeyError(e) {
  if (e?.name === "NotAllowedError") return "Cancelled, or the passkey prompt timed out.";
  if (e?.name === "OperationError") return "That didn't open your sessions' key. Try another passkey or your recovery code.";
  if (e?.name === "InvalidStateError") return "This device already has a passkey for your account.";
  return null;
}

// Adding a passkey: navigator.credentials.create() for the backend's registration options.
export async function createPasskey(options) {
  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: toBuffer(options.challenge),
      user: { ...options.user, id: toBuffer(options.user.id) },
      excludeCredentials: descriptors(options.excludeCredentials),
      // security keys only give a PRF output (lib/vault.js) for passkeys made asking for it
      extensions: { ...options.extensions, prf: {} },
    },
  });
  const r = credential.response;
  return {
    id: credential.id, rawId: toText(credential.rawId), type: credential.type,
    response: {
      clientDataJSON: toText(r.clientDataJSON), attestationObject: toText(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : [],
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    authenticatorAttachment: credential.authenticatorAttachment,
  };
}

// What goes back to the backend. A passkey's PRF output (lib/vault.js) never does: it's taken out here.
function assertion(credential) {
  const r = credential.response;
  const { prf, ...results } = credential.getClientExtensionResults() || {};
  return {
    id: credential.id, rawId: toText(credential.rawId), type: credential.type,
    response: {
      clientDataJSON: toText(r.clientDataJSON), authenticatorData: toText(r.authenticatorData),
      signature: toText(r.signature), userHandle: r.userHandle ? toText(r.userHandle) : null,
    },
    clientExtensionResults: results,
    authenticatorAttachment: credential.authenticatorAttachment,
  };
}

const prfOutput = (credential) => {
  const first = credential.getClientExtensionResults()?.prf?.results?.first;
  return first ? new Uint8Array(first) : null;
};

// Signing in or unlocking: navigator.credentials.get() for the backend's authentication options.
export async function answerWithPasskey(options) {
  const credential = await navigator.credentials.get({
    publicKey: { ...options, challenge: toBuffer(options.challenge), allowCredentials: descriptors(options.allowCredentials) },
  });
  return assertion(credential);
}

// The same, asking the passkey for its PRF output too (options.extensions.prf from backend/vault.py). Returns
// { credential, prf }: the credential for the backend, the PRF output (null when the passkey can't give one) for
// this page only.
export async function answerWithPasskeyPrf(options) {
  const salt = options.extensions?.prf?.eval?.first;
  const credential = await navigator.credentials.get({
    publicKey: {
      ...options, challenge: toBuffer(options.challenge), allowCredentials: descriptors(options.allowCredentials),
      extensions: { ...options.extensions, prf: { eval: { first: toBuffer(salt) } } },
    },
  });
  return { credential: assertion(credential), prf: prfOutput(credential) };
}

// A PRF output from one of these passkeys, with no backend involved: it only opens the private key in this page
// (setting up another passkey). Returns { credentialId, prf }.
export async function passkeyPrf(credentialIds, salt) {
  const credential = await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)), userVerification: "required",
      allowCredentials: credentialIds.map((id) => ({ type: "public-key", id: toBuffer(id) })),
      extensions: { prf: { eval: { first: toBuffer(salt) } } },
    },
  });
  return { credentialId: credential.id, prf: prfOutput(credential) };
}

export const NO_PRF = "This passkey can't protect sessions: its browser or password manager doesn't support it. Passkeys "
  + "in Google Password Manager, iCloud Keychain or Windows Hello (with an up-to-date browser) and most security keys can. "
  + "A security key added before may need adding again.";
